import React, { memo, useState } from 'react'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCaretUp, faCaretDown } from '@fortawesome/free-solid-svg-icons'
import { Link, useNavigate } from 'react-router-dom'

import { Table, TableHeader, TableRow, TableHeaderCell, TableBody, TableBodyCell } from '../../../lib/table'
import { resetSelectedUser, setSelectedUser } from '../../redux/features/selectedUser/selectedUserSlice'
import {
    useCompanyGetUsersQuery,
    useCompanyRemoveUserMutation,
    type ChargePointUserStatus,
} from '../../../common/redux/api/web-api'
import { filterFieldsBySearchValue } from '../../../lib/filter-fields-by-search-value'
import { PageLayoutContainer } from '../../../lib/page-layout-container'
import hasClaimForCompany from '../../../lib/hasClaimForCompany'
import { sortByString } from '../../../lib/sort-by-string'
import { WidgetLayout } from '../../../lib/widget-layout'
import { RootState } from '../../redux/store'
import { Button } from '../../../lib/button'
import Claims from '../../../lib/claims'
import Text from '../../../ui/Text'
import useText from '../../../common/hooks/useText'

import { notifyError, notifySuccess } from './../../../common/features/notificationsManager/Notify'

enum SortParameter {
    FIRSTNAME = 'firstName',
    EMAIL = 'email',
    MOBILE = 'mobile',
}

const SIcon = styled(FontAwesomeIcon)`
    margin-left: 4px;
`

export const UsersPage = memo(function AdminUsersWidget(): React.ReactElement {
    const text = useText()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const [searchValue, setSearchValue] = useState('')
    const [sortFilter, setSortFilter] = useState<{ parameter: SortParameter; reversed: boolean }>({
        parameter: SortParameter.EMAIL,
        reversed: false,
    })

    const user = useSelector((state: RootState) => state.user)
    const isAdmin = hasClaimForCompany(user, user.selectedCompanyId, Claims.Admin)
    const {
        data: users,
        isFetching: loading,
        refetch,
    } = useCompanyGetUsersQuery(
        { companyId: user?.selectedCompanyId },
        { skip: !user.selectedCompanyId, refetchOnMountOrArgChange: true }
    )
    const [removeUser] = useCompanyRemoveUserMutation()

    const removeUserValue = async (userId: string) => {
        if (confirm(text('msgRemoveUser')))
            try {
                await removeUser({ userId, companyId: user.selectedCompanyId }).unwrap()
                notifySuccess(text('msgUserRemoved'))
                refetch()
            } catch (error) {
                const errormessage = error?.data.messageCode
                if (errormessage === 'msgUserWithMDCantBeRemovedFromCompany') {
                    notifyError(text('msgUserWithMDCantBeRemovedFromCompany'))
                    return
                }
                if (errormessage === 'msgUserWithTagsCantBeRemovedFromCompany') {
                    notifyError(text('msgUserWithTagsCantBeRemovedFromCompany'))
                    return
                } else {
                    notifyError(text('msgUserNotRemoved'))
                    return
                }
            }
    }

    return (
        <PageLayoutContainer>
            <WidgetLayout
                title={<Text code="lblUsersPage"></Text>}
                headerButtons={[
                    isAdmin ? (
                        <Button
                            onClick={() => {
                                dispatch(resetSelectedUser())
                                navigate('/user')
                            }}
                            code="btnAdd"
                            data-testid="add-icon"
                            small
                        ></Button>
                    ) : (
                        <></>
                    ),
                ]}
            >
                <div className="row margin-bottom-small">
                    <div className="col-xs-12 col-md-4">
                        <input
                            data-testid="search"
                            type="text"
                            name="search"
                            id="search"
                            className="form-control"
                            value={searchValue}
                            onChange={(event) => setSearchValue(event.target.value)}
                            placeholder={text('msgSearch')}
                        />
                    </div>
                </div>

                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHeaderCell>
                                <a
                                    data-testid="name"
                                    onClick={() =>
                                        setSortFilter({
                                            parameter: SortParameter.FIRSTNAME,
                                            reversed: !sortFilter?.reversed,
                                        })
                                    }
                                    className="pointer"
                                >
                                    <Text code="lblName"></Text>
                                    {sortFilter?.parameter === SortParameter.FIRSTNAME && (
                                        <SIcon
                                            data-testid="icon-name"
                                            icon={sortFilter.reversed ? faCaretUp : faCaretDown}
                                        />
                                    )}
                                </a>
                            </TableHeaderCell>
                            <TableHeaderCell>
                                <a
                                    data-testid="email"
                                    onClick={() =>
                                        setSortFilter({
                                            parameter: SortParameter.EMAIL,
                                            reversed: !sortFilter?.reversed,
                                        })
                                    }
                                    className="pointer"
                                >
                                    <Text code="lblEmail"></Text>
                                    {sortFilter?.parameter === SortParameter.EMAIL && (
                                        <SIcon
                                            data-testid="icon-email"
                                            icon={sortFilter.reversed ? faCaretUp : faCaretDown}
                                        />
                                    )}
                                </a>
                            </TableHeaderCell>
                            <TableHeaderCell>
                                <a
                                    data-testid="mobile"
                                    onClick={() =>
                                        setSortFilter({
                                            parameter: SortParameter.MOBILE,
                                            reversed: !sortFilter?.reversed,
                                        })
                                    }
                                    className="pointer"
                                >
                                    <Text code="lblMobile"></Text>
                                    {sortFilter?.parameter === SortParameter.MOBILE && (
                                        <SIcon
                                            data-testid="icon-mobile"
                                            icon={sortFilter.reversed ? faCaretUp : faCaretDown}
                                        />
                                    )}
                                </a>
                            </TableHeaderCell>
                            <TableHeaderCell>
                                <Text code="lblStatus"></Text>
                            </TableHeaderCell>
                            <TableHeaderCell>
                                <Text code="lblRole"></Text>
                            </TableHeaderCell>
                            <TableHeaderCell>
                                <Text code="lblDelete"></Text>
                            </TableHeaderCell>
                        </TableRow>
                    </TableHeader>
                    {!loading && (
                        <TableBody>
                            {users
                                ?.filter((u) =>
                                    filterFieldsBySearchValue(
                                        [u.firstName, u.mobile, u.lastName, u.email, u.userStatus],
                                        searchValue
                                    )
                                )
                                .sort((a, b) =>
                                    sortByString(
                                        (a[sortFilter.parameter] as string) ?? '',
                                        (b[sortFilter.parameter] as string) ?? '',
                                        sortFilter.reversed
                                    )
                                )
                                .map((u) => (
                                    <TableRow key={u.id}>
                                        <TableBodyCell>
                                            <Link
                                                data-testid="name-user"
                                                onClick={() => {
                                                    dispatch(setSelectedUser(u))
                                                }}
                                                to={`/user/${u.id}`}
                                                className="pointer table btn-link"
                                            >
                                                {u.firstName} {u.lastName}
                                            </Link>
                                        </TableBodyCell>
                                        <TableBodyCell>{u.email}</TableBodyCell>
                                        <TableBodyCell>{u.mobile}</TableBodyCell>
                                        <TableBodyCell data-testid="table-body-cell-status">
                                            {u.userStatus === ('Valid' as ChargePointUserStatus)
                                                ? text('lblValidStatus')
                                                : text('lblInvalidStatus')}
                                        </TableBodyCell>
                                        <TableBodyCell>{u.roleInCompany}</TableBodyCell>
                                        <TableBodyCell>
                                            {isAdmin && (
                                                <div>
                                                    <button
                                                        data-testid="remove"
                                                        role="button"
                                                        className="btn btn-danger btn-xs margin-left-small btn-icon"
                                                        onClick={() => removeUserValue(u.id)}
                                                    >
                                                        <span className="fa fa-remove"></span>
                                                    </button>
                                                </div>
                                            )}
                                        </TableBodyCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    )}
                </Table>
                {loading && <div className="shimmer-large shimmer-container" />}
            </WidgetLayout>
        </PageLayoutContainer>
    )
})
